import { CareplanFilter, CareplanStatusFilter, RoDashboardTabs } from 'op-enums';
import { BaseConfigInterface } from './types';
import { BOOLEAN_OPTIONS, BOOLEAN_TBC_OPTIONS } from '../TreatmentInfoPage/Interface';

const demo_config: BaseConfigInterface = {
  showPreferredName: true,
  SpecialPhysicsConsultVisible: true,
  ErrorPopup: {
    Generic:
      "We encountered an error. Please refresh the page. If the issue persists, please contact Lumonus support: <a href='mailto:support@lumonus.com'>support@lumonus.com</a>",
    Practitioner:
      "We encountered an error. Please refresh the page. If the issue persists, please contact Lumonus support: <a href='mailto:support@lumonus.com'>support@lumonus.com</a>",
    Patient: 'We encountered an error. Please refresh the page. If the issue persists, please contact your clinic.',
  },
  ReferringPhysicians: {
    oncologist: 'Responsible Physician',
    generalPractitioner: 'Primary Physician (GP)',
    referringSurgeon: 'Referring Physician 1',
    referringSurgeon2: 'Referring Physician 2',
    referringSurgeon3: 'Referring Physician 3',
  },
  DefaultValue: {
    cied: '',
    patientStatus: 'unknown',
    previousRadioTreatment: '',
    treatmentRORequired: '',
    tumourName: 'tumor',
  },
  Clinex: {
    betaTesting: false,
  },
  Diagnosis: {
    defaultMStageTypeFilter: 'Clinical',
  },
  Intake: {
    disabled: false,
    isProstateDisabled: false,
    breastMandatoryFields: ['grade', 'er', 'pgr', 'her2'],
    lungMandatoryFields: ['weightLoss', 'smokingHistory', 'egfr', 'alk', 'pdl1'],
    headNeckMandatoryFields: ['P16', 'smokingHistory'],
    lymphomaMandatoryFields: ['stage', 'aOrBSymptoms', 'extranodal', 'bulkDisease'],
    breastFormVersion: 2,
    breastFormShowExercise: false,
  },
  PatientSummary: {
    isVisible: true,
    labsEnabled: false,
    RODocumentUpload: false,
    documentFilterField: 'description',
    hideInsuranceTab: false,
    summaryLabel: 'Patient Chart',
  },
  OutcomesPage: {
    isVisible: true,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  OTRPage: {
    isVisible: true,
    DateFormat: 'DD-MMM-YYYY',
    DateTimeFormat: 'DD MMM YYYY h:mma',
  },
  DisplayButton: {
    displaySimButton: true,
  },
  DisplayModal: {
    submission: false,
  },
  TreatmentInfoPage: {
    mandatoryFields: ['intent', 'treatingDepartment', 'cied', 'previousRadioTreatment'],
    ReadyForCareDate: false,
    AdditionalBookingInfo: false,
    MDT: false,
    AuthorizedICDTreatingDepartments: [],
    Hormones: false,
    Brachytherapy: false,
    ccip: false,
    TreatmentStartDate: false,
    ConsentStatus: false,
    PharmacyPrescription: false,
    PerformanceStatus: false,
  },
  SimulationPage: {
    SpacerGelWarning: false,
    imagingText: 'Add outside imaging request',
    imagingCountTitle: 'Outside imaging',
    disableExtentDefined: false,
    disablePositionSetup: false,
    immobilisationLabel: 'Immobilization',
  },
  PrescriptionPage: {
    MicrodosimetryTld: true,
    WeeklyPhysicsCheck: true,
    BasicDoseCalculation: false,
  },
  SubmissionPage: {
    PlanAimSubmissionType: 'Plan Aim',
    PlanAimHidden: false,
  },
  VolumingPage: {
    UsePrioritizedOARs: true,
    TargetValueDoseSuffixEnabled: false,
  },
  RadiationDashboard: {
    dateTimeFormat: 'MM/DD/YYYY hh:mm A',
    active: RoDashboardTabs.APPOINTMENTS,
    careplanDashboard: {
      careplanStatusFilters: [
        { id: CareplanStatusFilter.DRAFT, name: CareplanStatusFilter.DRAFT, checked: true },
        { id: CareplanStatusFilter.SIMULATION, name: CareplanStatusFilter.SIMULATION, checked: true },
        { id: CareplanStatusFilter.PLAN_AIM, name: CareplanStatusFilter.PLAN_AIM, checked: true },
        { id: CareplanStatusFilter.PRESCRIPTION, name: CareplanStatusFilter.PRESCRIPTION, checked: true },
        { id: CareplanStatusFilter.CPOT, name: CareplanStatusFilter.CPOT, checked: true },
      ],
      careplanFilters: [{ id: CareplanFilter.UNSUBMITTED, name: CareplanFilter.UNSUBMITTED, checked: false }],
      isPatientSummaryLinkEnabled: true,
    },
    showFilters: false,
  },
  QuestionLabel: {
    CIED: 'Cardiac Implantable Electronic Device (CIED)',
    RoRequired: 'RO Required',
    GoldSeeds: 'Gold Seeds',
    phase: 'Sequence',
  },
  QuestionOptions: {
    CIED: BOOLEAN_TBC_OPTIONS,
    previousRadioTreatment: BOOLEAN_OPTIONS,
  },
  QuestionValidationChildren: {
    CIED: ['ciedProtocolRequired', 'preTreatmentCiedCheckRequired'],
    Staging: {
      Primary: {
        RO: ['laterality', 'diagnosisDate'],
        MO: ['laterality'],
      },
      Metastasis: {
        RO: ['laterality', 'diagnosisDate'],
        MO: [],
      },
    },
  },
  ResetMutationVariableDefaults: {
    CIED: {
      ciedProtocolRequired: '',
      preTreatmentCiedCheckRequired: '',
      referralToGcCardiologist: '',
      otherCiedInstructions: '',
    },
    CiedProtocolRequired: {
      preTreatmentCiedCheckRequired: '',
      referralToGcCardiologist: '',
      otherCiedInstructions: '',
    },
  },
  GetMutationVariableDefaults: {
    CIED: {
      ciedProtocolRequired: 'yes',
      preTreatmentCiedCheckRequired: 'yes',
      referralToGcCardiologist: 'no',
      otherCiedInstructions: '',
    },
    CiedProtocolRequired: {
      preTreatmentCiedCheckRequired: 'yes',
      referralToGcCardiologist: 'no',
      otherCiedInstructions: '',
    },
  },
  DoseSiteSummary: {
    enabled: true,
    DateFormat: 'MM/DD/YYYY',
  },
  CardioReferralField: false,
  Tooltips: {
    hasPriority: false,
    hasClinicalPathologicalStaging: false,
  },
  UseDocumentTimezone: false,
  DefaultTimezone: 'America/New_York',
  DateFormat: 'MM DD YYYY',
  ShortDateFormat: 'MM/DD/YYYY',
  DiagnosisDateFormat: 'DD-MMM-YYYY',
  CareplanVoidDescription: 'Documentation will be voided in your OIS.',
  EmailMaxLength: 40,
  Outage: {
    link: '',
    showBanner: false,
    message: '',
    campaignId: '',
    startDate: '',
    endDate: '',
  },
  Notes: {
    DateTimeFormat: 'MM/DD/YYYY hh:mm A',
  },
  DefaultLocale: 'en',
  SupportEmail: 'support-us@lumonus.com',
  ShowResetDemoDataMenuLink: true,
  Insights: {
    mockData: true,
    chartDateFormat: 'MM/DD/YYYY',
    Dashboards: {
      careplanAdherence: true,
      techniques: true,
      averageFractions: true,
      totalReferrals: true,
      totalDiagnosis: true,
      totalPlans: true,
      linacs: true,
      simToTreatment: true,
      averageDailyPlans: true,
      totalConsultations: true,
      totalSimulations: true,
      totalTreatments: true,
    },
  },
  PatientCard: {
    appointmentDateFormat: 'ddd, MMM DD, YYYY',
    defaultDateFormat: 'MMM-DD-YYYY',
    defaultAlternateDateFormat: 'MMM DD, YYYY',
    dateOfBirthFormat: 'MMM-DD-YYYY',
  },
  AISummary: {
    lastUpdatedDateTimeFormat: 'MMM DD, YYYY, h:mm A',
  },
};

export default demo_config;
